import { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle2, ExternalLink } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useStudioStore } from '@/store/useStudioStore';
import { useDashboardStore } from '@/store/useDashboardStore';
import { siteApi } from '@/api/site';
import { projectApi } from '@/api/project';
import type { DnsConfigGuide, DomainStatusResponse, Site, SiteDomainSetupInfo } from '@/types/site';
import { trackEvent } from '@/api/track';
import { isSpecialProjectType } from '@/lib/projectUtils';
import { normalizeLegacyPlatformDomain, normalizeLegacyPlatformUrl } from '@/lib/platformDomain';
import { getTrackingErrorCode } from '@/lib/trackingUtils';

// ─── 域名解析工具（移植自原型 studio.html）───────────────────────────────────

const COMPOUND_SUFFIXES = new Set([
  'ac.uk',
  'co.id',
  'co.in',
  'co.jp',
  'co.kr',
  'co.nz',
  'co.uk',
  'com.au',
  'com.br',
  'com.cn',
  'com.hk',
  'com.mx',
  'com.sg',
  'com.tr',
  'gov.uk',
  'net.au',
  'net.cn',
  'org.au',
  'org.cn',
  'org.uk',
]);
const SHOW_LEGACY_DOMAIN_FAILURE_INLINE_WARNINGS = false;

function sanitizeDomain(raw: string) {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/^[a-z]+:\/\//i, '')
    .split('/')[0]
    .split('?')[0]
    .split('#')[0]
    .replace(/:\d+$/, '')
    .replace(/^\.+|\.+$/g, '');
}

interface ParsedDomain {
  host: string;
  root: string;
  subdomain: string;
}

function parseDomain(raw: string): ParsedDomain | null {
  const host = sanitizeDomain(raw);
  if (!host) return null;
  const labels = host.split('.').filter(Boolean);
  if (!labels.length) return null;
  if (labels.length === 1) return { host, root: host, subdomain: '' };
  const suffix = labels.slice(-2).join('.');
  const rootLength = COMPOUND_SUFFIXES.has(suffix) && labels.length >= 3 ? 3 : 2;
  if (labels.length <= rootLength) return { host, root: host, subdomain: '' };
  return {
    host,
    root: labels.slice(-rootLength).join('.'),
    subdomain: labels.slice(0, -rootLength).join('.'),
  };
}

function isWwwPrimaryDomain(raw: string) {
  return parseDomain(raw)?.subdomain === 'www';
}

/** 根域名模式：如果用户输入了子域名，自动裁剪为根域名并返回提示 */
function normalizeRoot(raw: string): { value: string; note: string } {
  const p = parseDomain(raw);
  if (!p) return { value: '', note: '' };
  if (p.subdomain === 'www') return { value: p.host, note: '' };
  if (!p.subdomain) return { value: p.root, note: '' };
  return {
    value: p.root,
    note: `You selected a root domain, so CoveAuto AI saved <strong>${p.root}</strong> instead.`,
  };
}

/** 子域名模式：如果用户输入了根域名，自动添加 lp. 前缀并返回提示；如果输入了 www 子域名，自动替换为 lp. 并提示 */
function normalizeSub(raw: string): { value: string; note: string } {
  const p = parseDomain(raw);
  if (!p) return { value: '', note: '' };
  if (p.subdomain === 'www') {
    return {
      value: p.host,
      note: `<strong>www</strong> can be connected as your primary domain. CoveAuto AI will switch this to root-domain setup when you save.`,
    };
  }
  if (p.subdomain) return { value: p.host, note: '' };
  const generated = `lp.${p.root}`;
  return {
    value: generated,
    note: `You selected a subdomain, so CoveAuto AI saved <strong>${generated}</strong> using <strong>lp</strong> as the default prefix.`,
  };
}

function mapDnsGuidesForDomain(
  guides: DnsConfigGuide[],
  domain: string,
  domainType?: string | null,
): DnsConfigGuide[] {
  if (domainType !== 'SUB') return guides;
  const prefix = parseDomain(domain)?.subdomain || '';
  if (!prefix) return guides;

  return guides.map((guide) => ({
    ...guide,
    hostRecord: guide.recordType === 'TXT' ? `_verify.${prefix}` : prefix,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * StudioSettingsDomain - 域名绑定设置组件
 *
 * 站点状态流转：
 *   0 = 未绑定自定义域名 → 可绑定
 *   1 = 待认证（已绑定，等待 DNS 验证）→ 只能验证 DNS
 *   2 = 认证成功 → 展示成功状态
 *   3 = 认证失败 → 可重新绑定
 */
interface StudioSettingsDomainProps {
  embedded?: boolean;
}

type DomainStatusNotice = {
  tone: 'success' | 'warning' | 'info';
  title: string;
  description: string;
};

const getDomainStatusValue = (value?: string | null) =>
  String(value || '')
    .trim()
    .toUpperCase();

const buildDomainStatusTrackingData = (status: DomainStatusResponse | null) => ({
  checkedDomain: status?.domain || null,
  checkedRootDomain: status?.rootDomain || null,
  registrationStatus: status?.registrationStatus || status?.status || null,
  registered: status?.registered ?? null,
  registrationCheckMethod: status?.registrationCheckMethod || null,
  dnsStatus: status?.dnsStatus || null,
  dnsConfigured: status?.dnsConfigured ?? null,
  dnsRecordTypes: status?.dnsRecordTypes || null,
  domainStatusMessage: status?.message || null,
});

const resolveDomainStatusNotice = (
  status: DomainStatusResponse,
): { canVerify: boolean; notice: DomainStatusNotice } => {
  const domain = status.domain || 'This domain';
  const rootDomain = status.rootDomain || domain;
  const registrationStatus = getDomainStatusValue(status.registrationStatus || status.status);
  const dnsStatus = getDomainStatusValue(status.dnsStatus);
  const recordTypes = status.dnsRecordTypes
    ? ` Detected DNS records: ${status.dnsRecordTypes}.`
    : '';
  const backendMessage = status.message ? ` ${status.message}` : '';

  if (status.registered === false || registrationStatus === 'UNREGISTERED') {
    return {
      canVerify: false,
      notice: {
        tone: 'warning',
        title: 'Domain is not registered yet',
        description: `${rootDomain} does not appear to be registered. Register or purchase this domain first, then save it here again and configure DNS.${backendMessage}`,
      },
    };
  }

  if (status.registered === null || registrationStatus === 'UNKNOWN') {
    return {
      canVerify: false,
      notice: {
        tone: 'warning',
        title: 'Domain registration could not be confirmed',
        description: `We could not confirm whether ${rootDomain} is registered. Check the domain spelling, confirm it is active at your registrar, then try DNS verification again.${backendMessage}`,
      },
    };
  }

  if (status.dnsConfigured === false || dnsStatus === 'NOT_CONFIGURED') {
    return {
      canVerify: false,
      notice: {
        tone: 'warning',
        title: 'DNS records were not found',
        description: `Add the required DNS records for ${domain} at your DNS provider, wait for propagation, then click Verify DNS again.${backendMessage}`,
      },
    };
  }

  if (status.dnsConfigured === null || dnsStatus === 'UNKNOWN') {
    return {
      canVerify: false,
      notice: {
        tone: 'warning',
        title: 'DNS status could not be confirmed',
        description: `We could not confirm public DNS records for ${domain}. Check that the records are public, wait a few minutes if you just changed DNS, then retry.${backendMessage}`,
      },
    };
  }

  return {
    canVerify: true,
    notice: {
      tone: 'success',
      title: 'Domain status check passed',
      description: `${domain} is registered and public DNS records are visible.${recordTypes} Continuing DNS verification.`,
    },
  };
};

const getDomainStatusErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object') {
    const apiError = (error as { apiError?: { value?: unknown; data?: unknown } }).apiError;
    const value = typeof apiError?.value === 'string' ? apiError.value.trim() : '';
    const message = error instanceof Error ? error.message : '';
    return value || message || 'Domain status check failed.';
  }

  return String(error || 'Domain status check failed.');
};

// Temporarily disable the blocking precheck before DNS verification and go straight to authSite.
const ENABLE_BLOCKING_DOMAIN_STATUS_PRECHECK = false;

export const StudioSettingsDomain = ({ embedded = false }: StudioSettingsDomainProps = {}) => {
  const { t } = useTranslation('studio');
  const currentProject = useStudioStore((s) => s.currentProject);
  const currentProjectTypeId = useStudioStore((s) => s.currentProjectTypeId);
  const currentSiteId = useStudioStore((s) => s.currentSiteId);
  const currentId = useStudioStore((s) => s.currentId);
  const setCurrentProjectData = useStudioStore((s) => s.setCurrentProjectData);
  const setCurrentSiteInfo = useStudioStore((s) => s.setCurrentSiteInfo);
  const setLastPublishedUrl = useStudioStore((s) => s.setLastPublishedUrl);
  const { fetchProjects, patchProjectUrl } = useDashboardStore();

  const [domainDedicated, setDomainDedicated] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [unbinding, setUnbinding] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [siteInfo, setSiteInfo] = useState<Site | null>(null);
  const [copiedRowIndex, setCopiedRowIndex] = useState<number | null>(null);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [delegateCopied, setDelegateCopied] = useState(false);
  const [dnsGuides, setDnsGuides] = useState<DnsConfigGuide[]>([]);
  const [domainSetupInfo, setDomainSetupInfo] = useState<SiteDomainSetupInfo | null>(null);
  const [domainStatusNotice, setDomainStatusNotice] = useState<DomainStatusNotice | null>(null);
  const pendingDomainSetupRefreshRef = useRef(false);
  const rebindKey = currentSiteId ? `domain_rebind_${currentSiteId}` : null;
  const pendingDnsSetupKey = currentSiteId ? `domain_pending_dns_setup_${currentSiteId}` : null;
  const [showRebindForm, setShowRebindFormState] = useState(() => {
    if (!currentSiteId) return false;
    return sessionStorage.getItem(`domain_rebind_${currentSiteId}`) === '1';
  });
  const [pendingDnsSetupDomain, setPendingDnsSetupDomainState] = useState(() => {
    if (!currentSiteId) return '';
    return sessionStorage.getItem(`domain_pending_dns_setup_${currentSiteId}`) || '';
  });

  const setShowRebindForm = (val: boolean) => {
    setShowRebindFormState(val);
    if (rebindKey) {
      if (val) sessionStorage.setItem(rebindKey, '1');
      else sessionStorage.removeItem(rebindKey);
    }
  };

  const setPendingDnsSetupDomain = (value: string) => {
    setPendingDnsSetupDomainState(value);
    if (pendingDnsSetupKey) {
      if (value) sessionStorage.setItem(pendingDnsSetupKey, value);
      else sessionStorage.removeItem(pendingDnsSetupKey);
    }
  };

  const handleStartRebind = () => {
    setShowRebindForm(true);
    setDomainInput('');
    setDomainStatusNotice(null);
    setDelegateOpen(false);
  };

  const syncProjectSiteDomain = useCallback(
    (nextSite: Site | null) => {
      if (!currentProject?.sitePaths?.length) return;
      const shouldSyncLiveDomain =
        !!nextSite &&
        (!nextSite.domain || nextSite.status === 2 || nextSite.sslCertStatus === 'ACTIVE');
      if (!shouldSyncLiveDomain) return;
      const effectiveSiteId = nextSite?.id ?? (currentSiteId ? Number(currentSiteId) : null);
      if (!effectiveSiteId || currentProject.sitePaths[0]?.siteId !== effectiveSiteId) return;

      const nextSitePaths = [...currentProject.sitePaths];
      const currentSitePath = nextSitePaths[0];
      nextSitePaths[0] = {
        ...currentSitePath,
        siteName: nextSite?.name || currentSitePath.siteName,
        domain: nextSite?.domain || '',
        originalDomain: nextSite?.originalDomain || currentSitePath.originalDomain,
      };

      setCurrentProjectData({
        ...currentProject,
        sitePaths: nextSitePaths,
      });

      const host = (
        nextSite?.domain ||
        nextSite?.originalDomain ||
        currentSitePath.originalDomain ||
        ''
      )
        .replace(/^https?:\/\//i, '')
        .replace(/\/+$/g, '');
      const normalizedHost = normalizeLegacyPlatformDomain(host);
      const normalizedPath = (currentSitePath.path || '').replace(/^\/+/, '').replace(/\/+$/, '');
      const liveUrl = normalizedHost
        ? normalizedPath
          ? `https://${normalizedHost}/${normalizedPath}`
          : `https://${normalizedHost}`
        : '';

      if (!liveUrl) return;

      setLastPublishedUrl(normalizeLegacyPlatformUrl(liveUrl));
      if (currentId) {
        localStorage.setItem(`pub_url_${currentId}`, normalizeLegacyPlatformUrl(liveUrl));
        patchProjectUrl(currentId, liveUrl);
      }
    },
    [
      currentId,
      currentProject,
      currentSiteId,
      patchProjectUrl,
      setCurrentProjectData,
      setLastPublishedUrl,
    ],
  );

  const loadFallbackDnsGuides = useCallback(
    async ({
      domain,
      domainType,
      projectTypeId,
    }: {
      domain: string;
      domainType?: string | null;
      projectTypeId?: number | null;
    }) => {
      if (!domainType) return [];

      try {
        const guides = await siteApi.getDnsGuideList({
          projectTypeId: projectTypeId ?? 999,
          domainType: domainType as 'ROOT' | 'SUB',
        });
        return mapDnsGuidesForDomain(guides, domain, domainType);
      } catch {
        return [];
      }
    },
    [],
  );

  const loadDomainSetupInfo = useCallback(
    async ({
      siteId,
      refresh = false,
      sourceSite = null,
    }: {
      siteId?: number | null;
      refresh?: boolean;
      sourceSite?: Site | null;
    } = {}) => {
      const effectiveSiteId =
        siteId ?? sourceSite?.id ?? siteInfo?.id ?? (currentSiteId ? Number(currentSiteId) : null);
      if (!effectiveSiteId) {
        setDomainSetupInfo(null);
        setDnsGuides([]);
        return null;
      }

      const fallbackDomain = sourceSite?.domain || siteInfo?.domain || '';
      const fallbackDomainType = sourceSite?.domainType || siteInfo?.domainType;
      const fallbackProjectTypeId =
        Number(
          sourceSite?.projectTypeId ??
            currentProjectTypeId ??
            currentProject?.projectTypeId ??
            currentProject?.projectType ??
            999,
        ) || 999;

      try {
        const info = await siteApi.getDomainSetupInfo({
          siteId: effectiveSiteId,
          refresh,
        });
        setDomainSetupInfo(info);
        const mappedGuides = mapDnsGuidesForDomain(
          info.dnsGuides || [],
          info.domain || fallbackDomain,
          info.domainType || fallbackDomainType,
        );
        if (mappedGuides.length) {
          setDnsGuides(mappedGuides);
          return info;
        }

        setDnsGuides(
          await loadFallbackDnsGuides({
            domain: info.domain || fallbackDomain,
            domainType: info.domainType || fallbackDomainType,
            projectTypeId: info.projectTypeId ?? fallbackProjectTypeId,
          }),
        );
        return info;
      } catch {
        const fallbackGuides = await loadFallbackDnsGuides({
          domain: fallbackDomain,
          domainType: fallbackDomainType,
          projectTypeId: fallbackProjectTypeId,
        });
        if (fallbackGuides.length) {
          setDnsGuides(fallbackGuides);
        }
        return null;
      }
    },
    [
      currentProject?.projectType,
      currentProject?.projectTypeId,
      currentProjectTypeId,
      currentSiteId,
      loadFallbackDnsGuides,
      siteInfo?.domain,
      siteInfo?.domainType,
      siteInfo?.id,
      siteInfo?.projectTypeId,
    ],
  );

  const loadSiteInfo = useCallback(async () => {
    if (!currentSiteId) return;
    try {
      const res = await siteApi.getSiteList({ pageNumber: 1, pageSize: 500 });
      const site = res.list.find((s) => s.id === Number(currentSiteId));
      setSiteInfo(site ?? null);
      setCurrentSiteInfo(site ?? null);
    } catch {
      // 静默失败，不影响其余 UI
    }
  }, [currentSiteId, setCurrentSiteInfo]);

  useEffect(() => {
    void loadSiteInfo();
  }, [loadSiteInfo]);

  // 当站点处于待认证/认证成功/失败状态时，预填输入框并同步 dedicated 开关
  useEffect(() => {
    if (!siteInfo) return;
    if (
      (siteInfo.status === 1 || siteInfo.status === 2 || siteInfo.status === 3) &&
      siteInfo.domain
    ) {
      setDomainInput(siteInfo.domain);
      setDomainDedicated(siteInfo.domainType === 'ROOT');
    }
  }, [siteInfo]);

  useEffect(() => {
    if (!currentSiteId) {
      setPendingDnsSetupDomainState('');
      return;
    }

    setPendingDnsSetupDomainState(
      sessionStorage.getItem(`domain_pending_dns_setup_${currentSiteId}`) || '',
    );
  }, [currentSiteId]);

  // 当站点进入待认证或认证失败状态时，从接口获取 DNS 配置指引列表
  useEffect(() => {
    if (
      !siteInfo?.domain ||
      (siteInfo.status !== 1 && siteInfo.status !== 2 && siteInfo.status !== 3)
    ) {
      pendingDomainSetupRefreshRef.current = false;
      setDomainSetupInfo(null);
      setDnsGuides([]);
      return;
    }

    const refresh = pendingDomainSetupRefreshRef.current;
    pendingDomainSetupRefreshRef.current = false;

    void loadDomainSetupInfo({
      siteId: siteInfo.id,
      refresh,
      sourceSite: siteInfo,
    });
  }, [loadDomainSetupInfo, siteInfo]);

  const refreshProject = async () => {
    const resolvedProjectTypeId =
      currentProjectTypeId ?? currentProject?.projectTypeId ?? currentProject?.projectType;
    const usesProjectApi = isSpecialProjectType(resolvedProjectTypeId);
    const hasId = usesProjectApi ? !!currentProject?.projectId : !!currentId;
    if (!hasId) return;
    try {
      const params = usesProjectApi
        ? { projectId: Number(currentProject!.projectId), tenantId: currentProject?.tenantId }
        : { userProjectId: Number(currentId) };
      const updated = await projectApi.getProjectDetail(params);
      if (!updated.sitePaths?.length && currentProject?.sitePaths?.length) {
        updated.sitePaths = currentProject.sitePaths;
      }
      setCurrentProjectData(updated);
    } catch {
      // 静默失败
    }
  };

  /** 失焦时规范化输入值（自动修正域名，提示由 liveNote 实时计算） */
  const handleDomainBlur = () => {
    if (!domainInput.trim()) return;
    const nextDomainDedicated = domainDedicated || isWwwPrimaryDomain(domainInput);
    if (nextDomainDedicated !== domainDedicated) {
      setDomainDedicated(nextDomainDedicated);
    }
    const result = nextDomainDedicated ? normalizeRoot(domainInput) : normalizeSub(domainInput);
    if (result.value) setDomainInput(result.value);
  };

  /** 实时派生提示：只要输入包含点且会被规范化，立即显示 */
  const liveNote = (() => {
    if (!domainInput.includes('.')) return '';
    const result = domainDedicated ? normalizeRoot(domainInput) : normalizeSub(domainInput);
    return result.note;
  })();

  const buildDomainTrackingData = (
    overrides: Record<string, unknown> = {},
    sourceSite: Site | null = siteInfo,
  ) => {
    const trackingDomain = normalizeLegacyPlatformDomain(
      String(overrides.domain ?? sourceSite?.domain ?? domainInput ?? ''),
    );
    const parsed = trackingDomain ? parseDomain(trackingDomain) : null;
    const resolvedProjectTypeId =
      currentProjectTypeId ?? currentProject?.projectTypeId ?? currentProject?.projectType ?? null;

    return {
      source: 'studio_settings_domain',
      embedded,
      userProjectId: currentId,
      projectId: currentProject?.projectId ?? null,
      projectTypeId: resolvedProjectTypeId,
      ...overrides,
      siteId: overrides.siteId ?? sourceSite?.id ?? (currentSiteId ? Number(currentSiteId) : null),
      domain: trackingDomain || null,
      rootDomain: parsed?.root ?? null,
      subdomain: parsed?.subdomain || null,
      domainType:
        overrides.domainType ?? sourceSite?.domainType ?? (domainDedicated ? 'ROOT' : 'SUB'),
      siteStatus: sourceSite?.status ?? null,
      sslCertStatus: sourceSite?.sslCertStatus ?? null,
      failureReason: sourceSite?.failureReason ?? null,
      failureReasonDetail: sourceSite?.failureReasonDetail ?? null,
      dnsRecordCount: dnsGuides.length,
      dnsRecordTypes: Array.from(new Set(dnsGuides.map((guide) => guide.recordType))).sort(),
    };
  };

  const handleSave = async () => {
    const effectiveSiteId = currentSiteId ?? (siteInfo?.id ? String(siteInfo.id) : null);
    if (!effectiveSiteId) {
      toast.error(t('settings.domain.toast.no_site'));
      return;
    }
    if (!domainInput.trim()) {
      toast.error(t('settings.domain.toast.no_domain'));
      return;
    }

    // 保存前规范化域名
    const savedFromRebind = showRebindForm;
    const nextDomainDedicated = domainDedicated || isWwwPrimaryDomain(domainInput);
    if (nextDomainDedicated !== domainDedicated) {
      setDomainDedicated(nextDomainDedicated);
    }
    const normalized = nextDomainDedicated ? normalizeRoot(domainInput) : normalizeSub(domainInput);
    const finalDomain = normalized.value || domainInput.trim();
    const hasSavedCustomDomain = !!(siteInfo?.domain?.trim() || domainSetupInfo?.domain?.trim());
    setDomainInput(finalDomain);

    setSaving(true);
    setDomainStatusNotice(null);
    try {
      let updatedSite: Site;
      if (hasSavedCustomDomain && !isAuthed) {
        updatedSite = await siteApi.updateSite({
          id: Number(effectiveSiteId),
          domain: finalDomain,
          domainType: nextDomainDedicated ? 'ROOT' : 'SUB',
        });
      } else {
        updatedSite = await siteApi.bindDomain({
          id: Number(effectiveSiteId),
          domain: finalDomain,
          domainType: nextDomainDedicated ? 'ROOT' : 'SUB',
        });
      }

      setSiteInfo(updatedSite);
      setCurrentSiteInfo(updatedSite);
      syncProjectSiteDomain(updatedSite);
      setDomainInput(updatedSite.domain || finalDomain);
      if (savedFromRebind) {
        setPendingDnsSetupDomain(normalizeLegacyPlatformDomain(updatedSite.domain || finalDomain));
      } else if (updatedSite.status === 1 || updatedSite.status === 2) {
        setPendingDnsSetupDomain('');
      }
      await refreshProject();
      setShowRebindForm(false);
      trackEvent({
        eventCode: 'domain_config',
        eventData: JSON.stringify(
          buildDomainTrackingData(
            {
              action: hasSavedCustomDomain && !isAuthed ? 'update' : 'bind',
              siteId: Number(effectiveSiteId),
              domain: updatedSite.domain || finalDomain,
              domainType: updatedSite.domainType,
              previousStatus: siteInfo?.status ?? null,
              nextStatus: updatedSite.status,
              previousSslCertStatus: siteInfo?.sslCertStatus ?? null,
              sslCertStatus: updatedSite.sslCertStatus ?? null,
              inputDomain: domainInput.trim(),
              normalizedDomain: finalDomain,
              normalizedFromInput: finalDomain !== domainInput.trim(),
            },
            updatedSite,
          ),
        ),
      });
      toast.success(t('settings.domain.toast.success'));
    } catch {
      toast.error(t('settings.domain.toast.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleUnbind = async () => {
    const effectiveSiteId = siteInfo?.id ?? (currentSiteId ? Number(currentSiteId) : null);
    if (!effectiveSiteId) return;
    setUnbinding(true);
    setDomainStatusNotice(null);
    try {
      const updatedSite = await siteApi.unbindDomain(effectiveSiteId);
      pendingDomainSetupRefreshRef.current = true;
      setSiteInfo(updatedSite);
      setCurrentSiteInfo(updatedSite);
      syncProjectSiteDomain(updatedSite);
      setShowRebindForm(false);
      setPendingDnsSetupDomain('');
      setDomainDedicated(false);
      setDomainInput('');
      setDomainSetupInfo(null);
      setDnsGuides([]);
      await refreshProject();
      toast.success(t('settings.domain.toast.unbind_success'));
    } catch {
      toast.error(t('settings.domain.toast.unbind_error'));
    } finally {
      setUnbinding(false);
    }
  };

  const handleVerifyDns = async () => {
    const effectiveSiteId = siteInfo?.id ?? (currentSiteId ? Number(currentSiteId) : null);
    if (!effectiveSiteId) {
      toast.error(t('settings.domain.toast.verify_no_site'));
      return;
    }
    const domainForStatusCheck = normalizeLegacyPlatformDomain(siteInfo?.domain || domainInput);
    if (!domainForStatusCheck) {
      toast.error(t('settings.domain.toast.no_domain'));
      return;
    }
    const isRetry = !!siteInfo?.domain;
    if (isRetry) {
      trackEvent({
        eventCode: 'Verification_retry',
        eventData: JSON.stringify(
          buildDomainTrackingData({
            siteId: effectiveSiteId,
            verifySourceStatus: siteInfo?.status ?? null,
            verifySourceSslCertStatus: siteInfo?.sslCertStatus ?? null,
          }),
        ),
      });
    }
    setPendingDnsSetupDomain('');
    setVerifying(true);
    const verifyStartedAt = performance.now();
    const previousStatus = siteInfo?.status ?? null;
    const previousSslCertStatus = siteInfo?.sslCertStatus ?? null;
    let checkedDomainStatus: DomainStatusResponse | null = null;
    let domainStatusCheckCompleted = false;
    try {
      if (ENABLE_BLOCKING_DOMAIN_STATUS_PRECHECK) {
        setDomainStatusNotice({
          tone: 'info',
          title: 'Checking domain status',
          description: `Checking whether ${domainForStatusCheck} is registered and has public DNS records before verification.`,
        });

        const domainStatus = await siteApi.checkDomainStatus({ domain: domainForStatusCheck });
        checkedDomainStatus = domainStatus;
        domainStatusCheckCompleted = true;
        const statusDecision = resolveDomainStatusNotice(domainStatus);
        setDomainStatusNotice(statusDecision.notice);

        trackEvent({
          eventCode: statusDecision.canVerify
            ? 'Domain_Status_Check_Passed'
            : 'Domain_Status_Check_Blocked',
          eventData: JSON.stringify(
            buildDomainTrackingData({
              result: statusDecision.canVerify ? 'passed' : 'blocked',
              failureStage: statusDecision.canVerify ? null : 'domain_status_check',
              siteId: effectiveSiteId,
              previousStatus,
              previousSslCertStatus,
              durationMs: Math.round(performance.now() - verifyStartedAt),
              ...buildDomainStatusTrackingData(domainStatus),
            }),
          ),
        });

        if (!statusDecision.canVerify) {
          toast.warning(statusDecision.notice.title);
          return;
        }
      }

      setDomainStatusNotice(null);
      const updatedSite = await siteApi.authSite(effectiveSiteId);
      const durationMs = Math.round(performance.now() - verifyStartedAt);
      pendingDomainSetupRefreshRef.current = true;
      setSiteInfo(updatedSite);
      setCurrentSiteInfo(updatedSite);
      syncProjectSiteDomain(updatedSite);
      await refreshProject();
      if (updatedSite.status === 2 || updatedSite.sslCertStatus === 'ACTIVE') {
        trackEvent({
          eventCode: 'Verification_Passed',
          eventData: JSON.stringify(
            buildDomainTrackingData(
              {
                result: 'passed',
                siteId: effectiveSiteId,
                previousStatus,
                nextStatus: updatedSite.status,
                previousSslCertStatus,
                sslCertStatus: updatedSite.sslCertStatus ?? null,
                precheckSkipped: !ENABLE_BLOCKING_DOMAIN_STATUS_PRECHECK,
                passedBy:
                  updatedSite.status === 2
                    ? 'site_status'
                    : updatedSite.sslCertStatus === 'ACTIVE'
                      ? 'ssl_cert_status'
                      : 'unknown',
                ...buildDomainStatusTrackingData(checkedDomainStatus),
                durationMs,
              },
              updatedSite,
            ),
          ),
        });
        toast.success(t('settings.domain.toast.verify_success'));
        fetchProjects();
      } else {
        setDomainStatusNotice({
          tone: 'warning',
          title: 'DNS verification still failed',
          description:
            'The domain is registered and public DNS records are visible, but CoveAuto AI could not verify the required DNS records yet. Confirm the records below are added exactly and retry after propagation.',
        });
        trackEvent({
          eventCode: 'Verification_Failed',
          eventData: JSON.stringify(
            buildDomainTrackingData(
              {
                result: 'not_verified',
                failureStage: 'auth_result',
                siteId: effectiveSiteId,
                previousStatus,
                nextStatus: updatedSite.status,
                previousSslCertStatus,
                sslCertStatus: updatedSite.sslCertStatus ?? null,
                precheckSkipped: !ENABLE_BLOCKING_DOMAIN_STATUS_PRECHECK,
                ...buildDomainStatusTrackingData(checkedDomainStatus),
                durationMs,
              },
              updatedSite,
            ),
          ),
        });
        toast.error(t('settings.domain.toast.verify_error'));
      }
    } catch (error) {
      trackEvent({
        eventCode: 'Verification_Failed',
        eventData: JSON.stringify(
          buildDomainTrackingData({
            result: 'api_error',
            failureStage:
              ENABLE_BLOCKING_DOMAIN_STATUS_PRECHECK && !domainStatusCheckCompleted
                ? 'domain_status_check_api'
                : 'auth_api',
            siteId: effectiveSiteId,
            previousStatus,
            previousSslCertStatus,
            errorCode: getTrackingErrorCode(error),
            domainStatusErrorMessage: getDomainStatusErrorMessage(error),
            ...buildDomainStatusTrackingData(checkedDomainStatus),
            precheckSkipped: !ENABLE_BLOCKING_DOMAIN_STATUS_PRECHECK,
            durationMs: Math.round(performance.now() - verifyStartedAt),
          }),
        ),
      });
      setDomainStatusNotice({
        tone: 'warning',
        title: 'DNS verification failed',
        description: `CoveAuto AI could not complete DNS verification for ${domainForStatusCheck}. Please check the required DNS records below and try again.`,
      });
      toast.error(t('settings.domain.toast.verify_error'));
    } finally {
      setVerifying(false);
    }
  };

  // 站点状态派生标志
  const siteStatus = siteInfo?.status ?? -99;
  const canBind = siteStatus === -99 || siteStatus === 0;
  const isPending = siteStatus === 1;
  const isAuthed = siteStatus === 2;
  const isFailed = siteStatus === 3;
  const currentSetupDomain =
    normalizeLegacyPlatformDomain(siteInfo?.domain || domainSetupInfo?.domain) || '';
  const isFreshSetupPending =
    isFailed &&
    !!currentSetupDomain &&
    sanitizeDomain(pendingDnsSetupDomain) === sanitizeDomain(currentSetupDomain);
  const showPendingLikeState = isPending || isFreshSetupPending;
  const showFailedGuideState = isFailed && !isFreshSetupPending;
  const showDnsSetupPanel = (showPendingLikeState || showFailedGuideState) && !showRebindForm;
  const showInputForm = canBind || isAuthed || showRebindForm;
  const savedCustomDomain =
    normalizeLegacyPlatformDomain(siteInfo?.domain || domainSetupInfo?.domain) || '';
  const showUnbindAction =
    !!savedCustomDomain &&
    !showRebindForm &&
    sanitizeDomain(domainInput) === sanitizeDomain(savedCustomDomain);

  const displayDomain =
    normalizeLegacyPlatformDomain(siteInfo?.domain || currentProject?.sitePaths?.[0]?.domain) ||
    null;
  const rawDefaultDomain =
    siteInfo?.originalDomain || currentProject?.sitePaths?.[0]?.originalDomain || null;
  const defaultDomain = rawDefaultDomain ? normalizeLegacyPlatformDomain(rawDefaultDomain) : null;

  // 从接口返回的 DNS 指引构建展示行
  // 一级域名（ROOT）：A 记录 + CNAME 记录共 2 行；二级域名（SUB）：CNAME 记录 1 行
  const dnsRows: { type: string; name: string; value: string; ttl: string }[] = dnsGuides.map(
    (g) => ({
      type: g.recordType,
      name: g.hostRecord,
      value: g.recordValue,
      ttl: g.ttl === 0 ? 'Auto' : String(g.ttl),
    }),
  );
  const embeddedDnsConfig = domainDedicated
    ? {
        type: 'root' as const,
        cardTitle: 'Root domain',
        cardCopy: 'For a domain dedicated to landing pages only.',
        otherCardTitle: 'Subdomain',
        otherCardCopy: 'For a site that already uses the main domain elsewhere.',
        emptyTitle: 'This domain is dedicated to landing pages, so connect a root domain.',
        emptyExample: 'Example: yourcompany.com',
        inputLabel: 'ROOT DOMAIN',
        inputPlaceholder: 'e.g. yourdomain.com',
        hostLabel: 'ROOT DOMAIN',
        recordLabel: 'A / CNAME / TXT',
      }
    : {
        type: 'sub' as const,
        cardTitle: 'Subdomain',
        cardCopy: 'For a site that already uses the main domain elsewhere.',
        otherCardTitle: 'Root domain',
        otherCardCopy: 'For a domain dedicated to landing pages only.',
        emptyTitle: 'This main domain is used elsewhere, so connect a subdomain for landing pages.',
        emptyExample: 'Example: landing.yourcompany.com',
        inputLabel: 'SUBDOMAIN',
        inputPlaceholder: 'e.g. lp.yourdomain.com',
        hostLabel: 'SUBDOMAIN',
        recordLabel: 'CNAME / TXT',
      };
  const embeddedDisplayDomain =
    displayDomain || domainInput || (domainDedicated ? 'yourdomain.com' : 'lp.yourdomain.com');
  const embeddedShowEditor = embedded && (canBind || showRebindForm);
  const embeddedShowRecords =
    embedded && (showPendingLikeState || showFailedGuideState || isAuthed) && !showRebindForm;
  const embeddedStatusText = isAuthed
    ? 'DNS verified'
    : showFailedGuideState
      ? 'DNS verification failed'
      : 'DNS verification pending';
  const registrarProviderName =
    domainSetupInfo?.matchedGuide?.providerName?.trim() ||
    domainSetupInfo?.domainLookup?.registrarName?.trim() ||
    '';
  const registrarGuideUrl = domainSetupInfo?.matchedGuide?.guideUrl?.trim() || '';
  const showRegistrarHint =
    (showPendingLikeState || showFailedGuideState || isAuthed) && !!registrarProviderName;
  const handleCopyRow = (value: string, index: number) => {
    navigator.clipboard.writeText(value);
    setCopiedRowIndex(index);
    setTimeout(() => setCopiedRowIndex(null), 2000);
  };

  const buildDelegateText = () => {
    const separator = '─────────────────────────────────';
    const recordLines = dnsRows
      .map((r) =>
        [`Type:  ${r.type}`, `Name:  ${r.name}`, `Value: ${r.value}`, `TTL:   ${r.ttl}`].join('\n'),
      )
      .join('\n\n');
    return [
      'Hi,',
      '',
      'Please configure the following DNS records for our domain. This is a one-time task - once the records are in place, no further changes are required.',
      '',
      `Domain: ${siteInfo?.domain ?? ''}`,
      '',
      'DNS Records to Add:',
      separator,
      recordLines,
      separator,
      '',
      "Please let me know once it's done so I can verify the connection on my end. Thank you!",
    ].join('\n');
  };

  const handleCopyDelegate = () => {
    navigator.clipboard.writeText(buildDelegateText());
    setDelegateCopied(true);
    setTimeout(() => setDelegateCopied(false), 2000);
  };

  const renderDomainStatusNotice = () => {
    if (!domainStatusNotice) return null;

    const toneClass =
      domainStatusNotice.tone === 'success'
        ? embedded
          ? 'border-[#BDE4D3] bg-[#F4FCF8] text-[#047857]'
          : 'border-[rgba(5,150,105,0.24)] bg-[rgba(5,150,105,0.08)] text-[#047857]'
        : domainStatusNotice.tone === 'info'
          ? embedded
            ? 'border-[#CFE5EE] bg-[#F7FBFD] text-[#0E7490]'
            : 'border-[rgba(8,145,178,0.2)] bg-[rgba(8,145,178,0.08)] text-[#0E7490]'
          : embedded
            ? 'border-[#F1DEC4] bg-[#FFF9F2] text-[#92400E]'
            : 'border-[rgba(217,119,6,0.2)] bg-[rgba(217,119,6,0.08)] text-[#92400E]';

    return (
      <div
        className={cn(
          'mb-[12px] rounded-[9px] border p-[11px_13px] text-[12.5px] leading-[1.6]',
          embedded
            ? 'rounded-[12px] px-[14px] py-[12px] shadow-[0_1px_2px_rgba(13,13,26,0.03)]'
            : '',
          toneClass,
        )}
      >
        <strong>{domainStatusNotice.title}</strong>
        <br />
        {domainStatusNotice.description}
      </div>
    );
  };

  // 通用 toggle track+thumb（对应原型 dom-toggle）
  const Toggle = ({ checked, disabled = false }: { checked: boolean; disabled?: boolean }) => (
    <div className="relative h-[20px] w-[36px] shrink-0">
      <div
        className={cn(
          'absolute inset-0 rounded-full transition-colors duration-200',
          checked ? 'bg-[#6D28D9]' : 'bg-black/[0.14]',
          disabled ? 'opacity-45' : '',
        )}
      />
      <div
        className={cn(
          'pointer-events-none absolute top-[3px] left-[3px] h-[14px] w-[14px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.22)] transition-transform duration-200',
          checked ? 'translate-x-[16px]' : '',
          disabled ? 'opacity-80' : '',
        )}
      />
    </div>
  );

  const renderDnsTable = () => {
    if (dnsRows.length === 0) return null;
    if (embedded) {
      return (
        <table className="dns-tbl">
          <thead>
            <tr>
              <th>{t('settings.domain.dns_record_type')}</th>
              <th>{t('settings.domain.dns_record_name')}</th>
              <th>{t('settings.domain.dns_record_value')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {dnsRows.map((row, idx) => (
              <tr key={`${row.type}-${row.name}-${idx}`}>
                <td>{row.type}</td>
                <td>{row.name}</td>
                <td>{row.value}</td>
                <td className="td-copy">
                  <button
                    type="button"
                    className="copy-btn"
                    aria-label={`Copy ${row.type} value`}
                    onClick={() => handleCopyRow(row.value, idx)}
                  >
                    <i
                      className={
                        copiedRowIndex === idx ? 'fa-solid fa-check' : 'fa-regular fa-copy'
                      }
                    />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return (
      <div
        className={cn(
          'mb-[14px]',
          embedded
            ? 'overflow-hidden rounded-[12px] border border-[#D9DEE8] bg-white shadow-[0_1px_2px_rgba(13,13,26,0.04)]'
            : '',
        )}
      >
        <div className={embedded ? 'overflow-x-auto' : ''}>
          <table className="w-full border-collapse text-[12px]" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: embedded ? '64px' : '60px' }} />
              <col style={{ width: embedded ? '30%' : '28%' }} />
              <col />
              <col style={{ width: embedded ? '56px' : '48px' }} />
            </colgroup>
            <thead>
              <tr>
                {[
                  t('settings.domain.dns_record_type'),
                  t('settings.domain.dns_record_name'),
                  t('settings.domain.dns_record_value'),
                  t('settings.domain.dns_record_ttl'),
                ].map((h) => (
                  <th
                    key={h}
                    className={cn(
                      'p-[8px_10px] text-left text-[11px] font-[700] tracking-[0.5px] uppercase',
                      embedded
                        ? 'border-b border-[#E8ECF3] bg-[#FBFCFE] text-[#0D0D1A]/50'
                        : 'border-b border-black/[0.07] bg-[#F7F7FC] text-[#0D0D1A]/56',
                    )}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dnsRows.map((row, idx) => (
                <tr key={idx}>
                  <td
                    className={cn(
                      'p-[8px_10px] font-mono text-[11.5px]',
                      embedded
                        ? 'border-b border-[#E8ECF3] align-top leading-[1.6] text-[#3A4254]'
                        : 'border-b border-black/[0.07] text-[#374151]',
                    )}
                  >
                    {row.type}
                  </td>
                  <td
                    className={cn(
                      'p-[8px_10px] font-mono text-[11.5px] break-all',
                      embedded
                        ? 'border-b border-[#E8ECF3] align-top leading-[1.6] text-[#3A4254]'
                        : 'border-b border-black/[0.07] text-[#374151]',
                    )}
                  >
                    {row.name}
                  </td>
                  <td
                    className={cn(
                      'p-[8px_10px] font-mono text-[11.5px]',
                      embedded
                        ? 'border-b border-[#E8ECF3] align-top leading-[1.6] text-[#3A4254]'
                        : 'border-b border-black/[0.07] text-[#374151]',
                    )}
                  >
                    <div
                      className={
                        embedded
                          ? 'flex flex-col items-start gap-2 sm:flex-row sm:items-center'
                          : 'flex items-center gap-2'
                      }
                    >
                      <span className="min-w-0 break-all">{row.value}</span>
                      <button
                        onClick={() => handleCopyRow(row.value, idx)}
                        className={cn(
                          'shrink-0 rounded-[6px] px-[8px] py-[3px] text-[11px] font-[700] transition-colors',
                          embedded
                            ? 'border border-[#D9DEE8] bg-[#FBFCFE] px-[9px] py-[4px] text-[#0D0D1A]/52 hover:border-[#6D28D9]/24 hover:bg-[#F5F2FF] hover:text-[#6D28D9]'
                            : 'border border-black/[0.07] bg-white text-[#0D0D1A]/56 hover:border-[#6D28D9]/30 hover:text-[#6D28D9]',
                        )}
                      >
                        {copiedRowIndex === idx
                          ? t('settings.domain.dns_record_copied')
                          : t('settings.domain.dns_record_copy')}
                      </button>
                    </div>
                  </td>
                  <td
                    className={cn(
                      'p-[8px_10px] font-mono text-[11.5px]',
                      embedded
                        ? 'border-b border-[#E8ECF3] align-top leading-[1.6] text-[#3A4254]'
                        : 'border-b border-black/[0.07] text-[#374151]',
                    )}
                  >
                    {row.ttl}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDomainRegistrarHint = () => {
    if (!showRegistrarHint) return null;

    return (
      <div
        className={cn(
          'mb-[12px] rounded-[12px] border p-[12px_13px]',
          embedded
            ? 'border-[#DDE5F0] bg-white shadow-[0_1px_2px_rgba(13,13,26,0.04)]'
            : 'border-[rgba(5,150,105,0.18)] bg-[linear-gradient(180deg,rgba(5,150,105,0.07),rgba(255,255,255,0.86))]',
        )}
      >
        <div
          className={cn(
            embedded
              ? 'flex items-start gap-3 sm:items-center sm:justify-between'
              : 'grid grid-cols-[30px_minmax(0,1fr)_auto] items-center gap-[10px]',
          )}
        >
          <div className={cn('flex min-w-0 items-start gap-3', embedded ? 'flex-1' : 'contents')}>
            <span
              className={cn(
                'inline-flex shrink-0 items-center justify-center text-[#059669]',
                embedded
                  ? 'mt-[1px] h-[22px] w-[22px] rounded-full bg-[#ECFDF5]'
                  : 'h-[30px] w-[30px] rounded-[10px] bg-[rgba(5,150,105,0.1)]',
              )}
            >
              <CheckCircle2 size={14} />
            </span>
            <div className={cn('min-w-0', embedded ? '' : 'self-center')}>
              <div className="text-[12.5px] leading-[1.35] font-[800] text-[#0D0D1A]">
                {t('settings.domain.provider_detected_title', {
                  provider: registrarProviderName,
                })}
              </div>
              <div className="mt-[2px] text-[11.5px] leading-[1.5] text-[#0D0D1A]/46">
                {t('settings.domain.provider_detected_desc')}
              </div>
            </div>
          </div>
          {registrarGuideUrl ? (
            <a
              href={registrarGuideUrl}
              target="_blank"
              rel="noreferrer"
              className={cn(
                embedded
                  ? 'inline-flex shrink-0 items-center gap-1 rounded-[8px] px-[2px] py-[1px] text-[12px] font-[700] text-[#2563EB] transition-opacity hover:opacity-80'
                  : 'inline-flex min-h-[32px] shrink-0 items-center gap-[7px] rounded-[9px] border border-[rgba(5,150,105,0.2)] bg-white px-[11px] text-[12px] font-[800] whitespace-nowrap text-[#059669] transition-colors hover:border-[rgba(5,150,105,0.34)] hover:bg-[rgba(5,150,105,0.06)]',
              )}
            >
              {t('settings.domain.provider_detected_link')}
              <ExternalLink size={13} />
            </a>
          ) : null}
        </div>
      </div>
    );
  };

  const renderEmbeddedContent = () => (
    <div className="dns-embedded-shell">
      {embeddedShowEditor ? (
        <div className="dns-empty-panel on">
          <div className="dns-domain-types">
            <button
              className={cn('dns-domain-type', !domainDedicated && 'on')}
              type="button"
              onClick={() => {
                setDomainDedicated(false);
                if (domainInput.trim()) {
                  const result = normalizeSub(domainInput);
                  if (result.value) setDomainInput(result.value);
                }
              }}
            >
              <strong>Subdomain</strong>
              <span>For a site that already uses the main domain elsewhere.</span>
            </button>
            <button
              className={cn('dns-domain-type', domainDedicated && 'on')}
              type="button"
              onClick={() => {
                setDomainDedicated(true);
                if (domainInput.trim()) {
                  const result = normalizeRoot(domainInput);
                  if (result.value) setDomainInput(result.value);
                }
              }}
            >
              <strong>Root domain</strong>
              <span>For a domain dedicated to landing pages only.</span>
            </button>
          </div>

          <div className="dns-empty-note">
            {embeddedDnsConfig.emptyTitle} <strong>{embeddedDnsConfig.emptyExample}</strong>
          </div>

          <div className="dns-empty-grid">
            <div className="dns-empty-field">
              <label>{embeddedDnsConfig.inputLabel}</label>
              <div className="dns-empty-row">
                <input
                  className="dns-empty-input"
                  type="text"
                  placeholder={embeddedDnsConfig.inputPlaceholder}
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onBlur={handleDomainBlur}
                />
                <button
                  className="dns-empty-apply"
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save domain'}
                </button>
              </div>
              <div
                className="dns-empty-hint"
                aria-live="polite"
                dangerouslySetInnerHTML={{ __html: liveNote || '&nbsp;' }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {embeddedShowRecords ? (
        <div className="dns-records-panel on">
          <div className="dns-record-host">
            <div className="dns-record-host-label">{embeddedDnsConfig.hostLabel}</div>
            <div className="dns-record-host-value">{embeddedDisplayDomain}</div>
          </div>

          {renderDomainRegistrarHint()}

          {renderDnsTable()}

          <p className="dns-note">
            Add the {embeddedDnsConfig.recordLabel} records below in your registrar, then verify
            DNS.
            <br />
            <strong>DNS changes can take up to 48 hours</strong> to propagate, but usually complete
            within minutes.
          </p>

          {renderDomainStatusNotice()}

          <div className="dns-verify-row">
            <div className={`dns-status${isAuthed ? 'ok' : ''}`}>
              <span className="st-dot" />
              {embeddedStatusText}
            </div>
          </div>

          <div className="auth-mini-actions" style={{ marginTop: 14 }}>
            <button
              className="ads-btn auth-main"
              type="button"
              onClick={handleVerifyDns}
              disabled={verifying || isAuthed}
            >
              {verifying ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" />
                  Checking...
                </>
              ) : isAuthed ? (
                <>
                  <i className="fa-solid fa-check" />
                  DNS verified
                </>
              ) : (
                <>
                  <i className="fa-solid fa-satellite-dish" />
                  Verify DNS now
                </>
              )}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  if (embedded) {
    return renderEmbeddedContent();
  }

  return (
    <div className="animate-in fade-in duration-200">
      {/* 标题 */}
      {!embedded ? (
        <>
          <div className="mb-[4px] text-[15px] font-[800] tracking-[-0.3px] text-[#0D0D1A]">
            {t('settings.domain.section_title')}
          </div>
          <div className="mb-[18px] text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
            {t('settings.domain.section_subtitle')}
          </div>
        </>
      ) : null}

      {/* 连接状态栏 */}
      <div
        className={cn(
          'mb-[14px] rounded-[10px] border p-[10px_12px] text-[12.5px] font-[600]',
          embedded
            ? 'mb-[18px] flex items-start gap-3 border-[#D9DEE8] bg-white px-[14px] py-[12px] shadow-[0_1px_2px_rgba(13,13,26,0.04)]'
            : 'flex items-center gap-2 border-black/[0.07] bg-[#F7F7FC]',
        )}
      >
        <span
          className={cn(
            'h-2 w-2 shrink-0 rounded-full',
            embedded ? 'mt-[5px]' : '',
            isAuthed ? 'bg-[#059669]' : 'bg-[#D97706]',
          )}
        />
        <span
          className={cn(
            'min-w-0 text-[#0D0D1A]',
            embedded
              ? 'leading-[1.55] break-words [&_strong]:mt-1 [&_strong]:block [&_strong]:break-all'
              : '',
          )}
          dangerouslySetInnerHTML={{
            __html:
              isAuthed && displayDomain
                ? t('settings.domain.status_connected', { domain: displayDomain })
                : showPendingLikeState && displayDomain
                  ? t('settings.domain.status_pending', { domain: displayDomain })
                  : showFailedGuideState && displayDomain
                    ? t('settings.domain.status_auth_failed', { domain: displayDomain })
                    : defaultDomain
                      ? t('settings.domain.status_disconnected_default', { domain: defaultDomain })
                      : t('settings.domain.status_disconnected'),
          }}
        />
      </div>

      {/* 认证失败提示 + DNS 记录展示 + 重新输入入口 */}

      {SHOW_LEGACY_DOMAIN_FAILURE_INLINE_WARNINGS && (
        <>
          <div
            className={cn(
              'mb-[14px] rounded-[9px] p-[11px_13px] text-[12.5px] leading-[1.6] text-[#92400E]',
              embedded
                ? 'mb-[18px] rounded-[12px] border border-[#F1DEC4] bg-[#FFF9F2] px-[14px] py-[12px] shadow-[0_1px_2px_rgba(13,13,26,0.03)]'
                : 'border border-[rgba(217,119,6,0.2)] bg-[rgba(217,119,6,0.08)]',
            )}
          >
            {t('settings.domain.status_auth_failed_hint')}
          </div>

          {/* DNS 记录表格（与待认证状态相同） */}
          {renderDnsTable()}

          {/* 重新验证按钮 */}
          {renderDomainStatusNotice()}

          <button
            className={cn(
              'flex w-full items-center justify-center gap-[7px] rounded-[9px] bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-[22px] py-[9px] text-[13px] font-[700] text-white transition-opacity hover:opacity-90 disabled:opacity-50',
              embedded ? 'mt-[2px] mb-[12px]' : 'mb-[10px]',
            )}
            onClick={handleVerifyDns}
            disabled={verifying}
          >
            <CheckCircle2 size={15} />
            {verifying ? t('settings.domain.verifying') : t('settings.domain.verify_dns')}
          </button>

          {/* 重新输入域名入口 */}
          <button
            className={cn(
              'w-full rounded-[9px] py-[9px] text-[12.5px] font-[600] transition-colors',
              embedded
                ? 'border border-[#D9DEE8] bg-white text-[#0D0D1A]/60 shadow-[0_1px_2px_rgba(13,13,26,0.03)] hover:border-[#6D28D9]/24 hover:bg-[#FBFCFE] hover:text-[#6D28D9]'
                : 'border border-black/[0.07] bg-[#F7F7FC] text-[#0D0D1A]/56 hover:border-[#6D28D9]/30 hover:text-[#6D28D9]',
            )}
            onClick={handleStartRebind}
          >
            {t('settings.domain.rebind_label')}
          </button>
        </>
      )}

      {/* 可绑定状态：专用切换 + 描述 + 输入表单 */}
      {showInputForm && (
        <>
          {/* 重新输入时的警告提示 */}
          {SHOW_LEGACY_DOMAIN_FAILURE_INLINE_WARNINGS && (
            <div
              className={cn(
                'mb-[14px] rounded-[9px] p-[11px_13px] text-[12.5px] leading-[1.6] text-[#92400E]',
                embedded
                  ? 'mb-[18px] rounded-[12px] border border-[#F1DEC4] bg-[#FFF9F2] px-[14px] py-[12px] shadow-[0_1px_2px_rgba(13,13,26,0.03)]'
                  : 'border border-[rgba(217,119,6,0.2)] bg-[rgba(217,119,6,0.08)]',
              )}
            >
              {t('settings.domain.status_auth_failed_hint')}
            </div>
          )}
          {/* 专用域名切换行（对应原型 dom-dedicated-row）：仅未绑定或重新输入时显示 */}
          {(canBind || showRebindForm) && (
            <>
              <div
                className={cn(
                  'mb-[10px] flex cursor-pointer justify-between rounded-[9px] border p-[10px_13px] select-none',
                  embedded
                    ? 'mb-[14px] items-start gap-4 border-[#D9DEE8] bg-white px-[14px] py-[12px] shadow-[0_1px_2px_rgba(13,13,26,0.04)]'
                    : 'items-center border-black/[0.07] bg-[#F7F7FC]',
                )}
                onClick={() => {
                  setDomainDedicated((v) => {
                    const next = !v;
                    if (domainInput.trim()) {
                      const result = next ? normalizeRoot(domainInput) : normalizeSub(domainInput);
                      if (result.value) setDomainInput(result.value);
                    }
                    return next;
                  });
                }}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-[600] text-[#0D0D1A]/56">
                    {t('settings.domain.dedicated_label')}
                  </div>
                  <div
                    className={cn(
                      'mt-[2px] text-[11.5px]',
                      embedded ? 'text-[#0D0D1A]/46' : 'text-[#0D0D1A]/38',
                    )}
                  >
                    {t('settings.domain.dedicated_desc')}
                  </div>
                </div>
                <Toggle checked={domainDedicated} />
              </div>

              {/* 动态说明（对应原型 domDedicatedDesc） */}
              <div
                className={cn(
                  'mb-[12px] text-[12px] leading-[1.6]',
                  embedded
                    ? 'mb-[18px] rounded-[9px] border border-[#E8ECF3] bg-[#FBFCFE] px-[14px] py-[12px] break-words text-[#0D0D1A]/50 [&_eg]:mt-1 [&_eg]:block [&_eg]:break-all'
                    : 'text-[#0D0D1A]/38',
                )}
              >
                <Trans
                  t={t}
                  i18nKey={
                    domainDedicated
                      ? 'settings.domain.dedicated_hint_root'
                      : 'settings.domain.dedicated_hint_sub'
                  }
                  components={{
                    eg: <span className="font-semibold text-[#0D0D1A]/56" />,
                  }}
                />
              </div>
            </>
          )}

          {/* 输入框 */}
          <div className={embedded ? 'mb-[18px]' : 'mb-[14px]'}>
            <label className="mb-[5px] block text-[12px] font-[700] text-[#0D0D1A]/56">
              {domainDedicated
                ? t('settings.domain.input_label_root')
                : t('settings.domain.input_label_sub')}
            </label>
            <div
              className={embedded ? 'flex flex-wrap items-center gap-3' : 'flex items-center gap-2'}
            >
              <input
                className={cn(
                  'h-[38px] flex-1 rounded-[8px] border border-black/[0.07] bg-white px-[12px] py-[9px] font-sans text-[13.5px] text-[#0D0D1A] transition-colors outline-none focus:border-[#6D28D9]/40',
                  embedded ? 'min-w-[280px]' : '',
                )}
                placeholder={
                  domainDedicated
                    ? t('settings.domain.placeholder_root')
                    : t('settings.domain.placeholder_sub')
                }
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onBlur={handleDomainBlur}
              />
              <button
                className={cn(
                  'shrink-0 rounded-[9px] bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-[16px] py-[9px] text-[12.5px] font-[700] whitespace-nowrap text-white transition-opacity hover:opacity-90 disabled:opacity-50',
                  embedded ? 'min-w-[96px]' : '',
                )}
                onClick={showUnbindAction ? handleUnbind : handleSave}
                disabled={showUnbindAction ? unbinding : saving}
              >
                {showUnbindAction
                  ? unbinding
                    ? t('settings.domain.unbinding')
                    : t('settings.domain.unbind')
                  : saving
                    ? t('settings.domain.saving')
                    : isPending
                      ? t('settings.domain.update')
                      : t('settings.domain.save')}
              </button>
            </div>
            {liveNote && (
              <div
                className={cn(
                  'mt-[6px] text-[11.5px] leading-[1.5] text-[#0D0D1A]/56',
                  embedded ? 'break-words' : '',
                )}
                dangerouslySetInnerHTML={{ __html: liveNote }}
              />
            )}
          </div>

          {isAuthed && !showRebindForm ? renderDomainRegistrarHint() : null}
        </>
      )}

      {/* 待认证：DNS 指引 + 表格 + 验证按钮 + 委托管理 */}
      {showDnsSetupPanel && (
        <>
          {showFailedGuideState && (
            <div
              className={cn(
                'mb-[14px] rounded-[9px] p-[11px_13px] text-[12.5px] leading-[1.6] text-[#92400E]',
                embedded
                  ? 'mb-[18px] rounded-[12px] border border-[#F1DEC4] bg-[#FFF9F2] px-[14px] py-[12px] shadow-[0_1px_2px_rgba(13,13,26,0.03)]'
                  : 'border border-[rgba(217,119,6,0.2)] bg-[rgba(217,119,6,0.08)]',
              )}
            >
              {t('settings.domain.status_auth_failed_hint')}
            </div>
          )}
          {renderDomainRegistrarHint()}
          {/* 说明框（对应原型 cfg-info-box info） */}
          <div
            className={cn(
              'mb-[14px] rounded-[9px] p-[11px_13px] text-[12.5px] leading-[1.6] text-[#0E7490]',
              embedded
                ? 'mb-[18px] rounded-[12px] border border-[#CFE5EE] bg-[#F7FBFD] px-[14px] py-[12px] shadow-[0_1px_2px_rgba(13,13,26,0.03)]'
                : 'border border-[rgba(8,145,178,0.2)] bg-[rgba(8,145,178,0.08)]',
            )}
          >
            <strong>{t('settings.domain.dns_title')}</strong>
            <br />
            {t('settings.domain.dns_desc')}
          </div>

          {/* DNS 表格（对应原型 cfg-dns-table） */}
          {renderDnsTable()}

          {/* 验证 DNS 按钮（对应原型全宽 cfg-save-btn） */}
          {renderDomainStatusNotice()}

          <button
            className={cn(
              'flex w-full items-center justify-center gap-[7px] rounded-[9px] bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-[22px] py-[9px] text-[13px] font-[700] text-white transition-opacity hover:opacity-90 disabled:opacity-50',
              embedded ? 'mt-[2px] mb-[14px]' : 'mb-[10px]',
            )}
            onClick={handleVerifyDns}
            disabled={verifying}
          >
            <CheckCircle2 size={15} />
            {verifying ? t('settings.domain.verifying') : t('settings.domain.verify_dns')}
          </button>

          {/* 其他人管理我的域名（对应原型 dom-delegate-row） */}
          <div
            className={cn(
              'flex cursor-pointer justify-between rounded-[9px] border p-[10px_13px] select-none',
              embedded
                ? 'items-start gap-4 border-[#D9DEE8] bg-white px-[14px] py-[12px] shadow-[0_1px_2px_rgba(13,13,26,0.04)]'
                : 'items-center border-black/[0.07] bg-[#F7F7FC]',
            )}
            onClick={() => setDelegateOpen((v) => !v)}
          >
            <span
              className={cn(
                'text-[12.5px] font-[600] text-[#0D0D1A]/56',
                embedded ? 'min-w-0 flex-1 leading-[1.5]' : '',
              )}
            >
              {t('settings.domain.delegate_label')}
            </span>
            <Toggle checked={delegateOpen} />
          </div>

          {/* 展开区：textarea + 复制按钮（对应原型 dom-delegate-reveal） */}
          {delegateOpen && (
            <div
              className={cn(
                'mt-[8px]',
                embedded
                  ? 'mt-[12px] rounded-[12px] border border-[#E8ECF3] bg-[#FBFCFE] p-[14px] shadow-[0_1px_2px_rgba(13,13,26,0.03)]'
                  : '',
              )}
            >
              <textarea
                className={cn(
                  'mb-[8px] block min-h-[210px] w-full resize-y rounded-[9px] border p-[12px_14px] font-mono text-[12px] leading-[1.75] text-[#0D0D1A] transition-colors outline-none focus:border-[#6D28D9]/35',
                  embedded
                    ? 'border-[#D9DEE8] bg-white shadow-[inset_0_1px_2px_rgba(13,13,26,0.03)]'
                    : 'border-black/[0.07] bg-[#F7F7FC]',
                )}
                readOnly
                value={buildDelegateText()}
              />
              <button
                className="mb-[14px] w-full rounded-[9px] bg-[linear-gradient(135deg,#6D28D9,#0891B2)] py-[10px] text-[13px] font-[700] text-white transition-opacity hover:opacity-90"
                onClick={handleCopyDelegate}
              >
                {delegateCopied
                  ? t('settings.domain.delegate_copied')
                  : t('settings.domain.delegate_copy')}
              </button>
            </div>
          )}
          <button
            className={cn(
              'mt-[8px] w-full rounded-[9px] py-[9px] text-[12.5px] font-[600] transition-colors',
              embedded
                ? 'border border-[#D9DEE8] bg-white text-[#0D0D1A]/60 shadow-[0_1px_2px_rgba(13,13,26,0.03)] hover:border-[#6D28D9]/24 hover:bg-[#FBFCFE] hover:text-[#6D28D9]'
                : 'border border-black/[0.07] bg-[#F7F7FC] text-[#0D0D1A]/56 hover:border-[#6D28D9]/30 hover:text-[#6D28D9]',
            )}
            onClick={handleStartRebind}
          >
            {t('settings.domain.rebind_label')}
          </button>
        </>
      )}

      {/* 认证成功：DNS 表格 + DNS Verified 按钮 + 解绑 */}
      {isAuthed && (
        <>
          {renderDnsTable()}
          <button
            className={cn(
              'mb-[10px] flex w-full cursor-default items-center justify-center gap-[7px] rounded-[9px] px-[22px] py-[9px] text-[13px] font-[700] text-[#059669]',
              embedded
                ? 'mb-0 rounded-[10px] border border-[#BDE4D3] bg-[#F4FCF8] shadow-[0_1px_2px_rgba(13,13,26,0.03)]'
                : 'border border-[rgba(5,150,105,0.3)] bg-[rgba(5,150,105,0.1)]',
            )}
            disabled
          >
            <CheckCircle2 size={15} />
            {t('settings.domain.dns_verified')}
          </button>
        </>
      )}
    </div>
  );
};
